import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Truck, CheckCircle2, AlertTriangle, Sun, Calendar, Download, Users,
  CircleSlash, FileMinus, BadgeCheck, ChevronRight, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  red: "bg-red-100 text-red-800 border-red-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
};

const ACTION_TONE = {
  green_active: "bg-emerald-600 text-white border-emerald-600",
  green_idle: "bg-white text-slate-700 border-slate-200 hover:bg-emerald-50 hover:border-emerald-300",
  amber_active: "bg-amber-500 text-white border-amber-500",
  amber_idle: "bg-white text-slate-700 border-slate-200 hover:bg-amber-50 hover:border-amber-300",
  red_active: "bg-red-600 text-white border-red-600",
  red_idle: "bg-white text-slate-700 border-slate-200 hover:bg-red-50 hover:border-red-300",
  blue_active: "bg-blue-600 text-white border-blue-600",
  blue_idle: "bg-white text-slate-700 border-slate-200 hover:bg-blue-50 hover:border-blue-300",
};

const STATUS_META = {
  present: { label: "Present", color: "emerald", icon: CheckCircle2 },
  half_day: { label: "Half-day", color: "amber", icon: ChevronRight },
  absent: { label: "Absent", color: "red", icon: CircleSlash },
  paid_leave: { label: "Paid Leave", color: "blue", icon: BadgeCheck },
  unpaid_leave: { label: "Unpaid Leave", color: "slate", icon: FileMinus },
};

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function monthIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function DriverAttendance() {
  const { user } = useAuth();
  const [tab, setTab] = useState("today");

  return (
    <div data-testid="driver-attendance-page" className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <Truck className="h-7 w-7 text-red-600 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Phase 7 · Logistics
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Driver Attendance
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {user?.name ? `Hi ${user.name}, ` : ""}mark daily attendance for all drivers
            (Logistics & Transport departments). One tap per status — no biometric, no
            leave ledger. Sundays / holidays are off-days unless you explicitly mark.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="today" data-testid="tab-today">Today / Date</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Monthly Summary</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4">
          <RosterTab />
        </TabsContent>
        <TabsContent value="summary" className="mt-4">
          <SummaryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
function RosterTab() {
  const [date, setDate] = useState(todayIso());
  const [roster, setRoster] = useState({ rows: [], total: 0, is_off_day: false });
  const [pending, setPending] = useState({ pending_count: 0, marked_count: 0, total_drivers: 0 });
  const [loading, setLoading] = useState(true);
  const [halfDayFor, setHalfDayFor] = useState(null);
  const [halfDayNote, setHalfDayNote] = useState("");
  const [leaveFor, setLeaveFor] = useState(null);

  const refresh = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/driver-attendance/roster", { params: { date } }),
        api.get("/driver-attendance/pending", { params: { date } }),
      ]);
      setRoster(r.data);
      setPending(p.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [date]);

  const onMark = async (driverId, status, note = null) => {
    try {
      await api.post("/driver-attendance/mark", {
        driver_id: driverId, date, status, note,
      });
      toast.success(`Marked ${STATUS_META[status].label}`);
      refresh(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mark failed");
    }
  };

  const onMarkAllPresent = async () => {
    try {
      const { data } = await api.post("/driver-attendance/mark-bulk", { date });
      const skipped = (data.skipped_locked || 0) + (data.skipped_collision || 0);
      toast.success(
        `Marked ${data.marked} present`
        + (skipped ? ` · ${skipped} skipped (locked/collision)` : ""),
      );
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk mark failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Pending to mark" value={pending.pending_count}
              tone="amber" icon={AlertTriangle} testid="dm-pending-count" />
        <Tile label="Marked" value={pending.marked_count} tone="green" icon={CheckCircle2} />
        <Tile label="Total drivers" value={pending.total_drivers} tone="slate" icon={Users} />
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Date</div>
          <Input type="date" value={date} max={todayIso()}
                 onChange={(e) => setDate(e.target.value)}
                 data-testid="dm-date-picker" className="mt-1" />
          {roster.is_off_day && (
            <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
              <Sun className="h-3 w-3" /> Sunday / holiday — off-day
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {roster.is_off_day ? (
            <span className="text-amber-700">
              Off-day: drivers are NOT auto-pending. Mark only if they actually worked.
            </span>
          ) : (
            <>{pending.pending_count} pending · click any status to mark</>
          )}
        </div>
        {pending.pending_count > 0 && !roster.is_off_day && (
          <Button onClick={onMarkAllPresent} className="brand-bg brand-bg-hover text-white"
                  data-testid="dm-mark-all-present">
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark all present ({pending.pending_count})
          </Button>
        )}
      </div>

      {!loading && roster.rows.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <Truck className="h-10 w-10 text-slate-300 mx-auto" />
          <h3 className="mt-3 text-sm font-semibold text-slate-700">No drivers configured</h3>
          <p className="text-xs text-slate-500 mt-1">
            Configure driver department(s) under settings, or add an employee to the
            Drivers / Logistics department.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {roster.rows.map((d) => (
          <DriverRow key={d.id} d={d}
                     onMark={(s) => onMark(d.id, s)}
                     onHalfDay={() => { setHalfDayFor(d); setHalfDayNote(""); }}
                     onLeave={() => setLeaveFor(d)} />
        ))}
      </div>

      {/* Half-day note dialog */}
      <Dialog open={!!halfDayFor} onOpenChange={(o) => !o && setHalfDayFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="halfday-dialog">
          <DialogHeader>
            <DialogTitle>Mark Half-day — {halfDayFor?.name}</DialogTitle>
            <DialogDescription>
              0.5 paid + 0.5 loss-of-pay. Add an optional note (e.g. "morning half only")
              for the audit trail.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={2} value={halfDayNote}
                    onChange={(e) => setHalfDayNote(e.target.value)}
                    data-testid="halfday-note-input"
                    placeholder="e.g. afternoon half only" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHalfDayFor(null)}>Cancel</Button>
            <Button className="brand-bg brand-bg-hover text-white"
                    data-testid="halfday-confirm-btn"
                    onClick={() => {
                      const id = halfDayFor.id;
                      onMark(id, "half_day", halfDayNote);
                      setHalfDayFor(null);
                    }}>Mark Half-day</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave choose dialog */}
      <Dialog open={!!leaveFor} onOpenChange={(o) => !o && setLeaveFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="leave-dialog">
          <DialogHeader>
            <DialogTitle>Mark Leave — {leaveFor?.name}</DialogTitle>
            <DialogDescription>
              <b>Paid leave</b>: counted as 1 paid day, no ledger debit.<br/>
              <b>Unpaid leave (LWP)</b>: 0 paid, full loss-of-pay (authorised).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="h-20 flex-col gap-1 border-blue-200"
                    data-testid="leave-paid-btn"
                    onClick={() => {
                      onMark(leaveFor.id, "paid_leave");
                      setLeaveFor(null);
                    }}>
              <BadgeCheck className="h-5 w-5 text-blue-600" />
              <span className="text-xs">Paid Leave</span>
              <span className="text-[10px] text-slate-500">1.0 paid day</span>
            </Button>
            <Button variant="outline" className="h-20 flex-col gap-1 border-slate-200"
                    data-testid="leave-unpaid-btn"
                    onClick={() => {
                      onMark(leaveFor.id, "unpaid_leave");
                      setLeaveFor(null);
                    }}>
              <FileMinus className="h-5 w-5 text-slate-600" />
              <span className="text-xs">Unpaid (LWP)</span>
              <span className="text-[10px] text-slate-500">0 paid · 1 LOP</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DriverRow({ d, onMark, onHalfDay, onLeave }) {
  const meta = d.status ? STATUS_META[d.status] : null;
  const initials = (d.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 flex-wrap"
         data-testid={`driver-row-${d.id}`}>
      <div className="h-10 w-10 rounded-full bg-red-100 text-red-700 font-semibold text-sm flex items-center justify-center">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-slate-900 truncate">{d.name}</div>
        <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
          <span className="font-mono">{d.employee_code}</span>
          <span>·</span>
          <span>{d.unit_name || "—"}</span>
          <span>·</span>
          <span>{d.department_name}</span>
        </div>
      </div>
      {meta && (
        <span data-testid={`driver-status-${d.id}`}
              className={`text-[10px] px-2 py-0.5 rounded font-medium border ${STATUS_TONE[meta.color]}`}>
          {meta.label}
        </span>
      )}
      <div className="flex gap-1.5 flex-wrap">
        <ActionBtn label="Present" tone="green" testid={`mark-present-${d.id}`}
                   active={d.status === "present"}
                   onClick={() => onMark("present")} />
        <ActionBtn label="Half" tone="amber" testid={`mark-halfday-${d.id}`}
                   active={d.status === "half_day"}
                   onClick={onHalfDay} />
        <ActionBtn label="Absent" tone="red" testid={`mark-absent-${d.id}`}
                   active={d.status === "absent"}
                   onClick={() => onMark("absent")} />
        <ActionBtn label="Leave" tone="blue" testid={`mark-leave-${d.id}`}
                   active={d.status === "paid_leave" || d.status === "unpaid_leave"}
                   onClick={onLeave} />
      </div>
    </div>
  );
}

function ActionBtn({ label, tone, active, onClick, testid }) {
  const cls = ACTION_TONE[`${tone}_${active ? "active" : "idle"}`];
  return (
    <button data-testid={testid} onClick={onClick}
            className={`text-xs px-3 py-1.5 rounded border font-medium transition-colors ${cls}`}>
      {label}
    </button>
  );
}

function Tile({ label, value, tone, icon: Icon, testid }) {
  const cls = { amber: "text-amber-700", green: "text-emerald-700", red: "text-red-700",
                slate: "text-slate-700", blue: "text-blue-700" }[tone] || "text-slate-700";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${cls}`} />}
      </div>
      <div data-testid={testid} className={`text-2xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function SummaryTab() {
  const [month, setMonth] = useState(monthIso());
  const [data, setData] = useState({ rows: [] });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/driver-attendance/summary", { params: { month } });
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [month]);

  const onExport = async () => {
    try {
      const resp = await api.get("/driver-attendance/export",
        { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `driver_attendance_${month.slice(0, 7)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-[10px]">Month</Label>
          <Input type="month" value={month.slice(0, 7)}
                 onChange={(e) => setMonth(`${e.target.value}-01`)}
                 className="h-9 w-44" />
        </div>
        <Button variant="outline" onClick={refresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
        <Button onClick={onExport} className="brand-bg brand-bg-hover text-white"
                data-testid="dm-export-btn">
          <Download className="h-3.5 w-3.5 mr-1" /> Excel
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-center px-2 py-2">Present</th>
              <th className="text-center px-2 py-2">Half</th>
              <th className="text-center px-2 py-2">Absent</th>
              <th className="text-center px-2 py-2">P.Leave</th>
              <th className="text-center px-2 py-2">U.Leave</th>
              <th className="text-center px-2 py-2 bg-red-50 text-red-700">Paid Days</th>
              <th className="text-center px-2 py-2">LOP</th>
            </tr>
          </thead>
          <tbody>
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-slate-400">
                No drivers / no data for this month.
              </td></tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`dm-summary-row-${r.id}`}>
                <td className="px-3 py-2.5">
                  <div className="text-sm font-medium">{r.name}</div>
                  <div className="text-[10px] text-slate-500">
                    <span className="font-mono">{r.employee_code}</span>
                    {" · "}{r.unit_name || ""}
                  </div>
                </td>
                <td className="text-center font-mono text-xs text-emerald-700">{r.present}</td>
                <td className="text-center font-mono text-xs text-amber-700">{r.half_day}</td>
                <td className="text-center font-mono text-xs text-red-700">{r.absent}</td>
                <td className="text-center font-mono text-xs text-blue-700">{r.paid_leave}</td>
                <td className="text-center font-mono text-xs text-slate-700">{r.unpaid_leave}</td>
                <td className="text-center font-mono text-sm font-bold bg-red-50 text-red-800">
                  {r.total_paid_days}
                </td>
                <td className="text-center font-mono text-xs text-red-700">{r.total_lop_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
